import { Heading } from "@/components/ui/heading";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { appDialogConfirm } from "@components";
import { AppTooltip } from "@components/AppTooltip";
import { useNavigate } from "@solidjs/router";
import { useAuth, userId } from "@state/auth";
import type { GoWatchVideoRow } from "@state/goWatch/api";
import { useGoWatch } from "@state/goWatch/state";
import { Edit, EllipsisVertical, MessageSquare, Play, Trash2 } from "lucide-solid";
import { Show } from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import AuthorFooter from "@features/communityContent/AuthorFooter";
import { TOPICS } from "@features/communityContent/topics";
import Chip from "../../Setups/Chip";
import { PROVIDER_META } from "../providerMeta";
import Thumbnail from "./Thumbnail";
import { useState } from "./state";

export default function Card(props: { video: GoWatchVideoRow }) {
  const navigate = useNavigate();
  const { setEditVideo } = useService(useState)();
  const { deleteVideo } = useService(useGoWatch)();
  const { isAdmin } = useService(useAuth)();

  const isOwner = () => isAdmin() || props.video.profile_id === userId();
  const topic = () => TOPICS[props.video.topic];
  const provider = () => PROVIDER_META[props.video.provider];

  return (
    <Box
      cursor='pointer'
      onClick={() => navigate(`/go-watch/${props.video.id}`)}
      bg='editorialPanel'
      borderRadius='16px'
      overflow='hidden'
      display='flex'
      flexDirection='column'
      class={css({
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--colors-editorial-hair)',
        transition: 'transform 0.22s, border-color 0.2s, box-shadow 0.25s',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: 'var(--colors-editorial-hair-strong)',
          boxShadow: '0 1px 2px rgba(20,22,27,.05), 0 18px 40px -24px rgba(20,22,27,.28)',
        },
        '&:hover .go-watch-play': { opacity: 1 },
      })}
    >
      <Box position='relative' aspectRatio='16/9' overflow='hidden' bg='#0d1117'>
        <Thumbnail seed={props.video.id} color={topic().color} thumbnailUrl={props.video.thumbnail_url} />
        <Box
          position='absolute'
          inset={0}
          zIndex={1}
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.32) 0%, transparent 26%, transparent 62%, rgba(0,0,0,.55) 100%)' }}
        />
        <Box position='absolute' top={0} left={0} right={0} height='3px' zIndex={2} style={{ background: topic().color }} />

        <Flex
          position='absolute'
          top='9px'
          left='9px'
          zIndex={3}
          alignItems='center'
          gap='5px'
          h='22px'
          px='8px'
          borderRadius='7px'
          bg='rgba(9,12,17,.72)'
          color='white'
          fontSize='10.5px'
          fontWeight='600'
        >
          <Dynamic component={provider().icon} size={12} />
          {provider().label}
        </Flex>

        <Show when={isOwner()}>
          <Box position='absolute' top='9px' right='9px' zIndex={3} onClick={(e) => e.stopPropagation()}>
            <Menu.Root
              onSelect={(details) => {
                if (details.value === 'edit') setEditVideo(props.video);
                if (details.value === 'delete') {
                  appDialogConfirm(
                    () => deleteVideo(props.video.id),
                    'Are you sure you want to delete this video?'
                  )();
                }
              }}
            >
              <Menu.Trigger
                asChild={(triggerProps) => (
                  <IconButton
                    size="2xs"
                    bg='rgba(9,12,17,.72)'
                    color='white'
                    borderWidth={0}
                    _hover={{ bg: 'rgba(9,12,17,.9)' }}
                    {...triggerProps()}
                  >
                    <EllipsisVertical size={14} />
                  </IconButton>
                )}
              />
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item minH={8} value="edit">
                      <Edit size={14} />
                      <Text textStyle="xs">Edit</Text>
                    </Menu.Item>
                    <Menu.Item minH={8} value="delete" color="error.10">
                      <Trash2 size={14} />
                      <Text textStyle="xs">Delete</Text>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>
        </Show>

        <Flex
          position='absolute'
          inset={0}
          zIndex={2}
          alignItems='center'
          justifyContent='center'
          opacity={0}
          transition='opacity 0.18s'
          class="go-watch-play"
        >
          <Flex
            w='52px'
            h='52px'
            borderRadius='full'
            bg='rgba(255,255,255,.94)'
            alignItems='center'
            justifyContent='center'
            boxShadow='0 6px 22px rgba(0,0,0,.4)'
          >
            <Play size={20} color='#0d1117' style={{ 'margin-left': '3px' }} fill='#0d1117' />
          </Flex>
        </Flex>
      </Box>

      <Grid gap='9px' p='12px 14px 13px' flex={1}>
        <Flex alignItems='center' gap='8px'>
          <Chip color={topic().color}>{topic().label}</Chip>
        </Flex>

        <Heading fontSize='14.5px' fontWeight='700' letterSpacing='-.01em' lineHeight='1.32' color='editorialInk' lineClamp={2} minHeight='2.6em'>
          {props.video.title}
        </Heading>

        <Flex alignItems='center' gap='9px' marginTop='auto'>
          <AuthorFooter
            username={props.video.author_username}
            id={props.video.profile_id}
            rank={props.video.author_role}
            date={props.video.created_at}
          />

          <Box flex='1' />

          <AppTooltip content={`${props.video.comment_count} comment${props.video.comment_count === 1 ? '' : 's'}`}>
            <Flex
              alignItems='center'
              gap='5px'
              h='24px'
              px='9px'
              borderRadius='7px'
              bg='editorialChip'
              color='editorialInk2'
              fontSize='11px'
              fontWeight='600'
              class={css({
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--colors-editorial-hair-strong)',
              })}
            >
              <MessageSquare size={13} />
              {props.video.comment_count}
            </Flex>
          </AppTooltip>
        </Flex>
      </Grid>
    </Box>
  );
}
